/**
 * useCliDetection — Detects available AI CLI backends (Claude + Codex).
 *
 * Calls the Electron IPC once on mount and caches the result.
 * Supports cache-reset via window event (for E2E testing).
 *
 * Re-exported as useClaudeCliDetection for backward compatibility.
 */

import { useState, useEffect, useCallback } from 'react';
import { isBrowserMode } from '../../browser-mode/browserModeUtils';

type CliDetectionEntry = { found: boolean; path: string | null };
type CliDetectionResult = { claude: CliDetectionEntry; codex: CliDetectionEntry };

const CACHE_RESET_EVENT = 'claude-cli-cache-reset';

let cachedResult: CliDetectionResult | null = null;

/** Reset the renderer-side cache and notify mounted hooks. Exposed for E2E testing. */
export function resetRendererCliCache() {
  cachedResult = null;
  window.dispatchEvent(new CustomEvent(CACHE_RESET_EVENT));
}

/**
 * Seed the renderer-side cache with a specific detection result and notify
 * mounted hooks. Exposed for E2E testing.
 *
 * A spec cannot override `window.electronAPI.aiChat.detectCli` to do this:
 * `contextBridge.exposeInMainWorld` hands the renderer a FROZEN object whose
 * properties are `writable: false, configurable: false`, so the assignment
 * silently no-ops in sloppy mode and the test falls through to the real
 * machine's CLI detection — MEASURED (`Object.isFrozen === true`, assignment
 * threw nothing, and the post-assignment identity was unchanged). Tests that
 * did that were asserting a property of the developer's `$PATH`.
 *
 * The main-process seams (`aiChat.forceClaudeNotFound` / `forceCodexNotFound`)
 * only force the NOT-found direction, so this is the seam for "pretend this CLI
 * is installed" without teaching the app to believe a binary exists that would
 * then be spawned.
 */
export function seedRendererCliCache(next: CliDetectionResult) {
  cachedResult = next;
  window.dispatchEvent(new CustomEvent(CACHE_RESET_EVENT));
}

// Expose for E2E tests
if (typeof window !== 'undefined') {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).__resetClaudeCliCache = resetRendererCliCache;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).__seedCliDetection = seedRendererCliCache;
}

export function useCliDetection() {
  const [result, setResult] = useState<CliDetectionResult | null>(cachedResult);
  const [isLoading, setIsLoading] = useState(!cachedResult);
  const [detectTrigger, setDetectTrigger] = useState(0);

  // Listen for cache-reset events so already-mounted hooks re-detect
  useEffect(() => {
    const handler = () => {
      setResult(null);
      setIsLoading(true);
      setDetectTrigger((c) => c + 1);
    };
    window.addEventListener(CACHE_RESET_EVENT, handler);
    return () => window.removeEventListener(CACHE_RESET_EVENT, handler);
  }, []);

  // Detect CLIs on mount or when triggered by cache reset
  useEffect(() => {
    if (cachedResult) {
      setResult(cachedResult);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const detect = async () => {
      try {
        const res = await window.electronAPI?.aiChat.detectCli();
        if (!cancelled && res) {
          // Handle both old single-CLI shape and new multi-CLI shape
          const normalized = normalizeDetectionResult(res);
          cachedResult = normalized;
          setResult(normalized);
        }
      } catch (err) {
        console.error('[useCliDetection] Detection failed:', err);
        if (!cancelled) {
          const fallback: CliDetectionResult = {
            claude: { found: false, path: null },
            codex: { found: false, path: null },
          };
          cachedResult = fallback;
          setResult(fallback);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    detect();
    return () => { cancelled = true; };
  }, [detectTrigger]);

  const anyFound = result?.claude.found || result?.codex.found || false;

  /**
   * The one predicate behind both the gate below and the enabled state of any
   * UI that fronts it. Browser mode passes because the sidebar there shows a
   * desktop-only CTA rather than a dead end.
   *
   * Anything that needs to know "can the user open AI chat?" must read this
   * rather than re-deriving it from `anyFound` — two independent copies of this
   * expression is exactly how the web demo ended up rendering AI task launchers
   * disabled next to an AI-chat toggle that opened fine.
   */
  const canOpenChat = isBrowserMode() || anyFound;

  const checkAndOpenSidebar = useCallback(
    (onCliFound: () => void, onCliNotFound: () => void) => {
      if (canOpenChat) {
        onCliFound();
      } else {
        onCliNotFound();
      }
    },
    [canOpenChat]
  );

  return {
    /** True when any CLI (Claude or Codex) is found */
    anyFound,
    /** @deprecated Use anyFound instead */
    isCliFound: anyFound,
    /** True when AI chat can be opened at all — what `checkAndOpenSidebar` decides on. */
    canOpenChat,
    isLoading,
    result,
    checkAndOpenSidebar,
  };
}

/** Backward compatibility alias */
export const useClaudeCliDetection = useCliDetection;

/**
 * Normalize detection result to multi-CLI shape.
 * Handles the old single-CLI shape `{ found, path }` for backward compatibility.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizeDetectionResult(res: any): CliDetectionResult {
  if (res && 'claude' in res && 'codex' in res) {
    return res as CliDetectionResult;
  }
  // Legacy single-CLI shape
  return {
    claude: { found: res?.found ?? false, path: res?.path ?? null },
    codex: { found: false, path: null },
  };
}
